import { zodResolver } from "@hookform/resolvers/zod";
import { pingRequestSchema } from "@pushup/shared";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@pushup/ui";
import { useForm } from "react-hook-form";
import { usePing } from "../hooks/use-ping";

interface PingFormValues {
  message: string;
}

/**
 * Demonstrates the validated POST flow end to end: React Hook Form + Zod
 * validation on the client, the shared schema validating on the server, and a
 * structured API error rendered on failure.
 */
export function PingForm() {
  const ping = usePing();
  const form = useForm<PingFormValues>({
    resolver: zodResolver(pingRequestSchema),
    defaultValues: { message: "" },
  });

  const onSubmit = form.handleSubmit((values) => {
    ping.mutate(values.message, {
      onSuccess: () => form.reset(),
    });
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Validated request</CardTitle>
        <CardDescription>
          POST /api/ping — the same Zod schema runs on client and server.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Label htmlFor="ping-message">Message</Label>
            <Input
              id="ping-message"
              placeholder="Hello, worker!"
              {...form.register("message")}
              aria-invalid={Boolean(form.formState.errors.message)}
            />
            {form.formState.errors.message ? (
              <p className="text-sm text-destructive">{form.formState.errors.message.message}</p>
            ) : null}
          </div>
          <div className="flex items-center gap-3">
            <Button type="submit" disabled={ping.isPending}>
              {ping.isPending ? "Sending…" : "Send"}
            </Button>
            {ping.data ? <Badge variant="success">Echo: {ping.data.echo}</Badge> : null}
          </div>
          {ping.isError ? (
            <p className="text-sm text-destructive">
              {ping.error instanceof Error ? ping.error.message : "Request failed."}
            </p>
          ) : null}
        </form>
      </CardContent>
    </Card>
  );
}
